import { SpinLoading } from 'antd-mobile';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import PermissionGuard from '@/components/guards/PermissionGuard';
import { ConstantUtil } from '@/utils/ConstantUtil';

// 懶加載頁面組件
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const Products = lazy(() => import('@/pages/Products'));
const AddProduct = lazy(() => import('@/pages/Products/AddProduct'));
const EditProduct = lazy(() => import('@/pages/Products/EditProduct'));
const ProductDrafts = lazy(() => import('@/pages/Products/ProductDrafts'));
const ProductDetail = lazy(() => import('@/pages/Products/ProductDetail'));
const Orders = lazy(() => import('@/pages/Orders'));
const OrderDetail = lazy(() => import('@/pages/Orders/OrderDetail'));
const Customers = lazy(() => import('@/pages/Customers'));
const Marketing = lazy(() => import('@/pages/Marketing'));
const AddMarketing = lazy(() => import('@/pages/Marketing/AddMarketing'));
const EditMarketing = lazy(() => import('@/pages/Marketing/EditMarketing'));
const MarketingAnalytics = lazy(() => import('@/pages/Marketing/MarketingAnalytics'));
const MarketingProducts = lazy(() => import('@/pages/Marketing/MarketingProducts'));
const MarketingPreview = lazy(() => import('@/pages/Marketing/MarketingPreview'));
const Categories = lazy(() => import('@/pages/Categories'));
const AddCategory = lazy(() => import('@/pages/Categories/AddCategory'));
const EditCategory = lazy(() => import('@/pages/Categories/EditCategory'));
const CategoryProducts = lazy(() => import('@/pages/Categories/CategoryProducts'));
const Brands = lazy(() => import('@/pages/Brands'));
const AddBrand = lazy(() => import('@/pages/Brands/AddBrand'));
const EditBrand = lazy(() => import('@/pages/Brands/EditBrand'));
const AddStore = lazy(() => import('@/pages/Brands/AddStore'));
const Pages = lazy(() => import('@/pages/Pages'));
const AddPage = lazy(() => import('@/pages/Pages/AddPage'));
const EditPage = lazy(() => import('@/pages/Pages/EditPage'));
const Members = lazy(() => import('@/pages/Members'));
const MemberView = lazy(() => import('@/pages/Members/MemberView'));
const MemberEdit = lazy(() => import('@/pages/Members/MemberEdit'));
const MemberTagManage = lazy(() => import('@/pages/Members/MemberTagManage'));
const MemberTags = lazy(() => import('@/pages/Members/MemberTags'));
const TagMembers = lazy(() => import('@/pages/Members/TagMembers'));
const MemberLevelConfig = lazy(() => import('@/pages/Members/MemberLevelConfig'));
const MemberLevelStatistics = lazy(() => import('@/pages/Members/MemberLevelStatistics'));
const MemberLevelUpgradeRules = lazy(() => import('@/pages/Members/MemberLevelUpgradeRules'));
const MemberLevelHistory = lazy(() => import('@/pages/Members/MemberLevelHistory'));
const MemberLevelBenefits = lazy(() => import('@/pages/Members/MemberLevelBenefits'));
const Banners = lazy(() => import('@/pages/Banners'));
const AddBanner = lazy(() => import('@/pages/Banners/AddBanner'));
const EditBanner = lazy(() => import('@/pages/Banners/EditBanner'));
const Contracts = lazy(() => import('@/pages/Contracts'));
const IssueShoppingCredit = lazy(() => import('@/pages/ShoppingCredits/IssueShoppingCredit'));
const ShoppingCreditUsage = lazy(() => import('@/pages/ShoppingCredits/ShoppingCreditUsage'));
const ShoppingCreditReports = lazy(() => import('@/pages/ShoppingCredits/ShoppingCreditReports'));
const AddContract = lazy(() => import('@/pages/Contracts/AddContract'));
const EditContract = lazy(() => import('@/pages/Contracts/EditContract'));
const Courses = lazy(() => import('@/pages/Courses'));
const AddCourse = lazy(() => import('@/pages/Courses/AddCourse'));
const EditCourse = lazy(() => import('@/pages/Courses/EditCourse'));
const CourseProducts = lazy(() => import('@/pages/Courses/CourseProducts'));
const Scheduling = lazy(() => import('@/pages/Scheduling'));
const AddSchedule = lazy(() => import('@/pages/Scheduling/AddSchedule'));
const Reconciliation = lazy(() => import('@/pages/Reconciliation'));
const Verification = lazy(() => import('@/pages/Verification'));
const PerformancePage = lazy(() => import('@/pages/Performance'));
const SupplementaryVerificationPage = lazy(() => import('@/pages/CustomerServices/SupplementaryVerification'));
const CancelVerificationPage = lazy(() => import('@/pages/CustomerServices/CancelVerification'));
const RefundRequestsPage = lazy(() => import('@/pages/CustomerServices/RefundRequests'));
const RefundPage = lazy(() => import('@/pages/CustomerServices/Refund'));
const TicketSearchPage = lazy(() => import('@/pages/CustomerServices/TicketSearch'));
const TicketListPage = lazy(() => import('@/pages/Tickets/TicketListPage'));
const TicketDetailPage = lazy(() => import('@/pages/Tickets/TicketDetailPage'));
const FinanceTransactions = lazy(() => import('@/pages/Finance/FinanceTransactions'));
const FinanceInvoices = lazy(() => import('@/pages/Finance/FinanceInvoices'));
const FinanceRefunds = lazy(() => import('@/pages/Finance/FinanceRefunds'));
const FinanceReports = lazy(() => import('@/pages/Finance/FinanceReports'));

// 加載組件
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <SpinLoading style={{ '--size': '48px' }} />
  </div>
);

// 路由守衛組件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// 公開路由組件（已登入用戶重定向到儀表板）
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// 路由配置
const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* 首頁重定向到登入頁面 */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 公開路由 */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />

          {/* 受保護的路由 */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/products" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PRODUCTS}>
                <Products />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-product" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PRODUCTS}>
                <AddProduct />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-product/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PRODUCTS}>
                <EditProduct />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/product-drafts" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PRODUCTS}>
                <ProductDrafts />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/product/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PRODUCTS}>
                <ProductDetail />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/orders" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.ORDERS}>
                <Orders />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/orders/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.ORDERS}>
                <OrderDetail />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/customers" element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          } />

          <Route path="/marketing" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <Marketing />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-marketing" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <AddMarketing />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-marketing/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <EditMarketing />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/marketing/:id/analytics" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <MarketingAnalytics />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/marketing/:id/products" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <MarketingProducts />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/marketing/:id/preview" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MARKETING}>
                <MarketingPreview />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/categories" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CATEGORIES}>
                <Categories />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-category" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CATEGORIES}>
                <AddCategory />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-category/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CATEGORIES}>
                <EditCategory />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/categories/:categoryId/products" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CATEGORIES}>
                <CategoryProducts />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/brands" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BRANDS}>
                <Brands />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-brand" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BRANDS}>
                <AddBrand />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-brand/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BRANDS}>
                <EditBrand />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-store" element={
            <ProtectedRoute>
              <AddStore />
            </ProtectedRoute>
          } />

          <Route path="/pages" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PAGES}>
                <Pages />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-page" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PAGES}>
                <AddPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-page" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PAGES}>
                <EditPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/members" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <Members />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/members/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberView />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/members/:id/edit" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberEdit />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/members/:memberId/tags" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberTagManage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-tags" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberTags />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-tags/:tagId/members" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <TagMembers />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-level-config" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberLevelConfig />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-level-statistics" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberLevelStatistics />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-level-upgrade-rules" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberLevelUpgradeRules />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-level-history" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberLevelHistory />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/member-level-benefits" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.MEMBERS}>
                <MemberLevelBenefits />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/banners" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BANNERS}>
                <Banners />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-banner" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BANNERS}>
                <AddBanner />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-banner/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.BANNERS}>
                <EditBanner />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/contracts" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CONTRACTS}>
                <Contracts />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-contract" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CONTRACTS}>
                <AddContract />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-contract/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CONTRACTS}>
                <EditContract />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/courses" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.COURSES}>
                <Courses />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-course" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.COURSES}>
                <AddCourse />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/edit-course/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.COURSES}>
                <EditCourse />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/course/:courseId/products" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.COURSES}>
                <CourseProducts />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/scheduling" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SCHEDULING}>
                <Scheduling />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/add-schedule" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SCHEDULING}>
                <AddSchedule />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/reconciliation" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.RECONCILIATION}>
                <Reconciliation />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/verification" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.VERIFICATION}>
                <Verification />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/performance" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.PERFORMANCE}>
                <PerformancePage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          {/* 客服管理路由 */}
          <Route path="/customer-service/supplementary-verification" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CUSTOMER_SERVICE_SUPPLEMENTARY_VERIFICATION}>
                <SupplementaryVerificationPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/customer-service/cancel-verification" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CUSTOMER_SERVICE_CANCEL_VERIFICATION}>
                <CancelVerificationPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/customer-service/refund-requests" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CUSTOMER_SERVICE}>
                <RefundRequestsPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/customer-service/refund" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CUSTOMER_SERVICE_REFUND}>
                <RefundPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/customer-service/ticket-search" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.CUSTOMER_SERVICE_TICKET_SEARCH}>
                <TicketSearchPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          {/* 票券管理路由 */}
          <Route path="/tickets" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.TICKETS}>
                <Navigate to="/tickets/list" replace />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/tickets/list" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.TICKETS}>
                <TicketListPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/tickets/detail/:id" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.TICKETS}>
                <TicketDetailPage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          {/* 財務管理路由 */}
          <Route path="/finance" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.FINANCE}>
                <Navigate to="/finance/transactions" replace />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/finance/transactions" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.FINANCE_TRANSACTIONS_VIEW}>
                <FinanceTransactions />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/finance/invoices" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.FINANCE_INVOICES_MANAGE}>
                <FinanceInvoices />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/finance/refunds" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.FINANCE_REFUNDS_MANAGE}>
                <FinanceRefunds />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/finance/reports" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.FINANCE_REPORTS_VIEW}>
                <FinanceReports />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          {/* 購物金管理路由 */}
          <Route path="/shopping-credits" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SHOPPING_CREDITS}>
                <Navigate to="/shopping-credits/issue" replace />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/shopping-credits/issue" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SHOPPING_CREDITS_ISSUE}>
                <IssueShoppingCredit />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/shopping-credits/usage" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SHOPPING_CREDITS_VIEW}>
                <ShoppingCreditUsage />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          <Route path="/shopping-credits/reports" element={
            <ProtectedRoute>
              <PermissionGuard requiredPermission={ConstantUtil.PERMISSIONS.SHOPPING_CREDITS_REPORTS}>
                <ShoppingCreditReports />
              </PermissionGuard>
            </ProtectedRoute>
          } />

          {/* 404 路由 - 重定向到登入頁面 */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;
